import { ErrorModal, MesfModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  IStatusPanelParams,
  MenuItemDef,
  RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { TextFieldSearchFilter } from "../../../../../shared/TextFieldSearchFilter";
import { CatalogGrade } from "../../../models/CatalogGrade";
import { deleteCatalogGrade } from "../../../repositories/CatalogGradeRepository";
import { useGradeOptionsFunctions } from "../hooks/useGradesOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { GradeDetails } from "./GradeDetails";

const AddGradeStatusPanel = ({
  handleNewGrade,
}: {
  handleNewGrade: () => void;
}) => {
  return (
    <Button
      variant="contained"
      color="primary"
      fullWidth
      style={{ margin: "0.5rem 0" }}
      onClick={handleNewGrade}
    >
      NEW Grade
    </Button>
  );
};

type Props = {
  isLoading: boolean;
  getCatalogGradesFromAPI: Function;
  gradeId: string | undefined;
};

export const TableGrade = ({
  isLoading,
  getCatalogGradesFromAPI,
  gradeId,
}: Props) => {
  const navigate = useNavigate();

  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [grade, setGrade] = useState<CatalogGrade | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [error, setError] = useState("");

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewGrade = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setGrade(null);
    navigate(`/configuration/steel-grades/new`);
  };
  const { getMenuOptions } = useGradeOptionsFunctions({
    setGrade,
    setShowModal,
    handleNewGrade,
  });
  const { rows, columnDefs } = useTableData({
    setGrade,
    setShowModal,
    showContextMenu,
  });

  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: any) => {
      setShowModal("edit");
      setGrade(data);
    },
  });

  const onRowSelected = (event: RowSelectedEvent) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setGrade(event.data);
      setShowModal("edit");
      navigate(
        `/configuration/steel-grades/${(event.data as CatalogGrade).GradeId}`,
      );
    }
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "gradeContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({
    shouldUpdate,
    close,
    entityId,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => {
    if (close) {
      setShowModal("");
      setGrade(null);
      if (gridApi) gridApi?.deselectAll();
    }
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
          setGrade(rowNode?.data);
        }
      }, 0);
    }
    navigate(`/configuration/steel-grades`);
  };

  useEffect(() => {
    if (gradeId) {
      if (gradeId === "new") {
        handleNewGrade();
      } else {
        let gradeNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if ((n.data as CatalogGrade).GradeId === Number(gradeId)) {
            gradeNode = n;
            return true;
          }
        });

        if (gradeNode!) {
          gridApi?.ensureIndexVisible(gradeNode.rowIndex ?? 0, "middle");
          gradeNode.setSelected(true);
          setGrade(gradeNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "gradeContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Grades
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ margin: "10px 18px" }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          style={{ width: "100%", padding: "0 10px" }}
          spacing={2}
        >
          <Grid item md={4} xs={12}>
            <div style={{ height: "75vh", width: "100%" }}>
              <AgGridReact<CatalogGrade>
                rowData={rows || []}
                columnDefs={columnDefs}
                defaultColDef={columnSimpleDefaults}
                rowHeight={34}
                headerHeight={34}
                animateRows
                rowSelection="single"
                getContextMenuItems={getContextMenuItems}
                onGridReady={(params) => setGridApi(params.api)}
                getRowId={(params) => String(params.data.GradeId)}
                loading={isLoading}
                onRowSelected={onRowSelected}
                gridOptions={{
                  statusBar: {
                    statusPanels: [
                      {
                        statusPanel: "agTotalRowCountComponent",
                        align: "right",
                      },
                      {
                        statusPanel: () => (
                          <AddGradeStatusPanel
                            handleNewGrade={handleNewGrade}
                          />
                        ),
                        align: "left",
                      },
                    ],
                  },
                }}
              />
            </div>
          </Grid>
          <Grid item md={8} xs={12}>
            <div style={{ height: "75vh", width: "100%" }}>
              <GradeDetails
                grade={grade}
                modal={showModal}
                refreshData={getCatalogGradesFromAPI}
                onHide={onModalHide}
              />
            </div>
          </Grid>
        </Grid>
      </Grid>
      <MesfModal
        title={`DELETE GRADE`}
        open={showModal === "delete"}
        handleClose={() => setShowModal("")}
        maxWidth="md"
      >
        <MesfModal.Content style={{ padding: "15px 30px" }}>
          {isLoading && <CenteredLazyLoading />}
          {!isLoading && (
            <>
              <Grid container spacing={1}>
                <Grid item md={12} xs={12}>
                  <Typography>
                    Are you sure you want to delete{" "}
                    <strong>{grade?.GradeName}</strong> grade?
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </MesfModal.Content>
        <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => setShowModal("")}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={async () => {
                  const resp = await deleteCatalogGrade(grade?.GradeId!);
                  if (!resp.ok) {
                    setError(resp.message);
                  }
                  getCatalogGradesFromAPI();
                }}
              >
                DELETE
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
